import { useEffect, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import { TooltipProvider } from '../components/ui/tooltip';
import { startOpenPanel } from './openpanel';
import { startGoogleAds } from './googleAds';
import { startPostHog } from './posthog';
import { captureAffiliateReferral } from './affiliateReferral';
import { recordAffiliateClick } from '../features/affiliates/repository';

// App-wide providers, ported from the old main.tsx. Rendered inside the SSR
// document shell (__root). Every analytics init here is client-only - they touch
// browser globals, so they must never run during SSR.
//
// Clerk is deliberately NOT here. It mounts per-surface through
// `auth/ClerkScope`, so prerendered marketing routes ship no auth script at
// all; see that file for why.

/** Run `fn` once the browser is idle (or shortly after), never during SSR. */
function onIdle(fn: () => void): () => void {
  const w = window as typeof window & {
    requestIdleCallback?: (cb: () => void) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(fn);
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, 2000);
  return () => window.clearTimeout(id);
}

// PostHog is initialized after first paint, off the critical path, so its
// session-recorder and surveys bundles don't block the landing page. Because
// init is deferred to idle, session replay can run normally - its recorder
// bundle loads after the page is interactive, not before. Surveys stay off at
// init and can be started later only where they're actually needed.
//
// `startPostHog` owns the config and the exit flush WebKit needs, and registers
// that listener before init, so the whole ordering rides the deferral intact.
// Init mutates the `posthog` singleton in place and deliberately sets no React
// state: the singleton is already the identity `PostHogProvider` hands to
// `usePostHog()` consumers, so there is nothing to re-render.
function useClientPostHogInit() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return onIdle(startPostHog);
  }, []);
}

// OpenPanel rides the same idle deferral as PostHog: it is marketing analytics,
// so nothing about it belongs on the critical path. Its own module guards
// against SSR and against a second init, so this effect stays a bare call.
function useClientOpenPanelInit() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return onIdle(() => {
      void startOpenPanel();
    });
  }, []);
}

// The affiliate referral is the one capture that does NOT ride the idle
// deferral, because it is not a third-party script. It reads a query parameter
// and sets a cookie, which is local work costing microseconds, and a missed
// referral costs an affiliate a commission rather than an analytics event. On
// load, a visitor who bounces immediately is still attributed.
//
// Arriving on the link is also the only moment the affiliate's own console can
// learn that anybody followed it: the cookie is invisible to us until that
// visitor starts a checkout, which most never do. So the arrival is reported
// once, here, and it is fire-and-forget - a tally on somebody else's console
// must never become this reader's error.
function useClientAffiliateCapture() {
  useEffect(() => {
    const code = captureAffiliateReferral();
    if (code) recordAffiliateClick(code);
  }, []);
}

// Google Ads conversion tracking rides the same idle deferral. It is paid-ads
// measurement, so it belongs even further from the critical path than the other
// two, and its own module guards against SSR and against a second init.
function useClientGoogleAdsInit() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return onIdle(startGoogleAds);
  }, []);
}

export function Providers({ children }: { children: ReactNode }) {
  useClientPostHogInit();
  useClientOpenPanelInit();
  useClientGoogleAdsInit();
  useClientAffiliateCapture();

  // PostHogProvider is mounted unconditionally, on the server too. It is a bare
  // context provider (it only self-initializes when handed an `apiKey`, which we
  // never do), so mounting it early costs nothing and keeps the element tree
  // identical before and after init. Adding the provider later instead pushed
  // the whole app down a level, which unmounted and remounted every page - the
  // router then re-applied its stored scroll offset and yanked a reader who had
  // scrolled during those first seconds back to the top.
  return (
    <PostHogProvider client={posthog}>
      <TooltipProvider>{children}</TooltipProvider>
    </PostHogProvider>
  );
}
